/**
 * Shared comparator for SmartImprove finding codes (e.g. INB01, OUT02, QLT01).
 *
 * Findings are sorted **by prefix first, then by numeric suffix** so the
 * Operational Findings table and every "Linked findings" sub-list group
 * codes logically (INB01..INB03, then OUT01..OUT03, then QLT01..) instead
 * of interleaving them by trailing number only.
 *
 * The prefix order mirrors the logical operational flow declared by
 * `OPERATIONAL_AREAS` in `backend/smartimprove_routes.py`:
 *   ORG → INB → OUT → INT → AUT → INF → RPT → YRD → LAB → QLT → FUT.
 *
 * Unknown / legacy prefixes sort after every known prefix, preserving
 * stable ordering for migrated rows that haven't been backfilled yet.
 */
const FINDING_PREFIX_ORDER = [
  "ORG",
  "INB",
  "OUT",
  "INT",
  "AUT",
  "INF",
  "RPT",
  "REP", // legacy alias for RPT (Reporting) — groups with its canonical sibling
  "YRD",
  "LAB",
  "QLT",
  "FUT",
];

const _prefixRank = (code) => {
  const m = (code || "").match(/^([A-Z]+)/);
  if (!m) return FINDING_PREFIX_ORDER.length + 1;
  const idx = FINDING_PREFIX_ORDER.indexOf(m[1]);
  return idx === -1 ? FINDING_PREFIX_ORDER.length : idx;
};

const _numericSuffix = (code) => {
  const m = (code || "").match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
};

/** Comparator: sort findings by (prefix_rank, numeric_suffix). */
export function compareFindingCodes(aCode, bCode) {
  const pr = _prefixRank(aCode) - _prefixRank(bCode);
  if (pr !== 0) return pr;
  return _numericSuffix(aCode) - _numericSuffix(bCode);
}

/** Convenience: comparator over finding-shaped objects with `.finding_code`. */
export function compareFindings(a, b) {
  return compareFindingCodes(a?.finding_code, b?.finding_code);
}
